import services from '@/services/ascentiaServiceIndex'
import { FLEET_CONSTS as FDC } from '../consts'
import { daysDifference, getFormattedDateTime } from '@/utilities/dates'
import _groupBy from 'lodash/groupBy'

const state = {
  fleetData: [],
  openMaintenanceData: [],
  flightDeckEffectsData: [],
  healthScoreByTailData: [],
  healthScoreByFeatureData: [],
  healthScoreTrendData: {},
  healthScorePhmAlertData: [],
  healthScoreMxRecOverlaysData: [],
  selectedAirlines: [],
}

const getters = {
  mappedFleetData: state => {
    return state.fleetData
  },

  mappedOpenMaintenanceData: state => {
    const mapped = state.openMaintenanceData.map(item => {
      return {
        issueId: item.id,
        statusName: item.status.name,
        systemName: item.system?.name,
        sourceName: item.source.name,
        alertCount: item.alertCount,
        lastAlertDate: item.lastAlertDate,
        tailNumber: item.tail.name,
        airlineTierTypeId: item.airlineAgreementTypeId,
      }
    })
    return mapped
  },

  mappedFlightDeckEffectsData: state => {
    return state.flightDeckEffectsData
  },

  mappedHealthScoreByTailData: state => {
    return state.healthScoreByTailData
  },

  mappedHealthScoreByFeatureData: state => {
    return state.healthScoreByFeatureData
  },

  mappedHealthScoreTrendData: state => {
    return state.healthScoreTrendData
  },

  mappedHealthScorePhmAlertData: state => {
    return state.healthScorePhmAlertData
  },

  mappedHealthScoreMxRecOverlaysData: state => {
    return state.healthScoreMxRecOverlaysData
  },

  mappedAirlineAgreementTypeIds: state => {
    const airlineAgreementTypeIds = []

    state.selectedAirlines.map(item => {
      airlineAgreementTypeIds.push(...item.phmAgreementTypes)
    })

    return [...new Set(airlineAgreementTypeIds)]
  },
}

const mutations = {
  [FDC.UPDATE_FLEET_DATA]: (state, data) => (state.fleetData = data),
  [FDC.UPDATE_OPEN_MAINTENANCE_DATA]: (state, data) =>
    (state.openMaintenanceData = data),
  [FDC.UPDATE_FLIGHT_DECK_EFFECTS_DATA]: (state, data) =>
    (state.flightDeckEffectsData = data),
  [FDC.UPDATE_HEALTH_SCORE_BY_TAIL_DATA]: (state, data) =>
    (state.healthScoreByTailData = data),
  [FDC.UPDATE_HEALTH_SCORE_BY_FEATURE_DATA]: (state, data) =>
    (state.healthScoreByFeatureData = data),
  [FDC.UPDATE_HEALTH_SCORE_TREND_DATA]: (state, data) =>
    (state.healthScoreTrendData = data),
  [FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA]: (state, data) =>
    (state.healthScorePhmAlertData = data),
  [FDC.UPDATE_HEALTH_SCORE_MX_REC_OVERLAYS_DATA]: (state, data) =>
    (state.healthScoreMxRecOverlaysData = data),
}

const actions = {
  async [FDC.GET_FLIGHT_DECK_EFFECTS_DATA]({ commit }, {flightNumber, tailNumber}) {
    const fdeResponse = await services.fleet.getData(
      'flightDeckEffectDetailData',
      {flightNumber, tailNumber},
      true,
    )
    const fdeData = fdeResponse.data
      .filter(item => item.messageType === 'FDE')
      .map(item => {
        return {
          ...item,
          errorType: item.messageType,
          timeStamp: item.timeAlerted,
        }
      })

    commit(FDC.UPDATE_FLIGHT_DECK_EFFECTS_DATA, fdeData)
  },

  async [FDC.GET_OPEN_MAINTENANCE_DATA]({ commit, rootGetters }, statuses) {
    try {
      // get global filter, user settings and lookups data
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
      const gfSelections = rootGetters['globalFilter/selections']
      const airlinesData = rootGetters['user/mappedUserAirlines']
      const phmAirlinesData =
        rootGetters['user/mappedUserAirlinesWithPhmAgreements']
      const tailsData = rootGetters['lookups/mappedLuTails']

      // set allowed airlineIds - airlineIds with Phm Agreements
      const allowedAirlineIds = phmAirlinesData.map(item => item.airlineId)

      // get filtered tails
      const filteredTails = tailsData
        .filter(item => allowedAirlineIds.includes(item.airlineId))
        .filter(
          item =>
            gfSelections.length === 0 ||
            gfSelections.filter(
              selection =>
                selection.airlineId === item.airlineId &&
                selection.masterModelId === item.masterModelId,
            ).length > 0,
        )

      // get airlineNames
      const filteredAirlines = airlinesData
        .filter(
          item =>
            airlineIds.length === 0 || airlineIds.includes(item.airlineId),
        )
        .filter(item => allowedAirlineIds.includes(item.airlineId))
      const filteredAirlineIds = filteredAirlines.map(item => item.airlineId)
      const filteredAirlineNames = filteredAirlines.map(item => item.icaoCode)

      // Save filtered and selected airlines
      state.selectedAirlines = filteredAirlines

      // get issue data
      const params = {
        airlineNames: filteredAirlineNames,
        statuses,
      }

      const issueResponse = await services.fleet.getData(
        'maintenanceIssuesData',
        params,
        true,
      )

      const issueData = issueResponse.data
        .filter(
          item =>
            filteredTails.filter(tail => tail.text === item.tail.name).length >
            0,
        )
        .filter(item => filteredAirlineIds.includes(item.airline.id))
        .filter(
          item =>
            masterModelIds.length === 0 ||
            masterModelIds.includes(item.masterModel.id),
        )

      commit(FDC.UPDATE_OPEN_MAINTENANCE_DATA, issueData)
    } catch (error) {
      console.log(`${[FDC.GET_OPEN_MAINTENANCE_DATA]}: error ${error}`)
    }
  },

  async [FDC.GET_FLEET_DATA]({ commit, rootGetters }, statuses) {
    try {
      // get global filter, user settings and lookups data
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
      const gfSelections = rootGetters['globalFilter/selections']
      const airlinesData = rootGetters['user/mappedUserAirlines']
      const phmAirlinesData =
        rootGetters['user/mappedUserAirlinesWithPhmAgreements']
      const tailsData = rootGetters['lookups/mappedLuTails']
      const masterModelsData = rootGetters['lookups/mappedLuMasterModels']

      // set allowed airlineIds - airlineIds with Phm Agreements
      const allowedAirlineIds = phmAirlinesData.map(item => item.airlineId)

      // get filtered tails
      const filteredTails = tailsData
        .filter(item => allowedAirlineIds.includes(item.airlineId))
        .filter(
          item =>
            gfSelections.length === 0 ||
            gfSelections.filter(
              selection =>
                selection.airlineId === item.airlineId &&
                selection.masterModelId === item.masterModelId,
            ).length > 0,
        )

      // get airlineNames
      const filteredAirlines = airlinesData
        .filter(
          item =>
            airlineIds.length === 0 || airlineIds.includes(item.airlineId),
        )
        .filter(item => allowedAirlineIds.includes(item.airlineId))
      const filteredAirlineIds = filteredAirlines.map(item => item.airlineId)
      const filteredAirlineNames = filteredAirlines.map(item => item.icaoCode)

      // Save filtered and selected airlines
      state.selectedAirlines = filteredAirlines

      // get connectivity data
      const connectivityResponse = await services.fleet.getData(
        'connectivityData',
        filteredAirlineIds,
        true,
      )
      const connectivityData = connectivityResponse.data.map(item => {
        return {
          ...item,
          lastUpdated: item.lastUpdated + 'Z',
        }
      })

      // get issue data
      const params = {
        airlineNames: filteredAirlineNames,
        statuses,
      }

      const issueResponse = await services.fleet.getData(
        'maintenanceIssuesData',
        params,
        true,
      )
      const issueData = issueResponse.data
        .filter(
          item =>
            filteredTails.filter(tail => tail.text === item.tail.name).length >
            0,
        )
        .filter(item => filteredAirlineIds.includes(item.airline.id))
        .filter(
          item =>
            masterModelIds.length === 0 ||
            masterModelIds.includes(item.masterModel.id),
        )

      // get fde data
      const fdeResponse = await services.fleet.getData(
        'flightDeckEffectSummaryData',
        filteredAirlineIds,
        true,
      )
      const fdeData = fdeResponse.data

      // build fleetData
      const data = filteredTails.map(tail => {
        const [airline] = airlinesData.filter(
          al => tail.airlineId === al.airlineId,
        )
        const airlineRec = {
          airlineName: airline?.name || '',
          airlineIcaoCode: airline?.icaoCode || '',
        }
        const [masterModel] = masterModelsData.filter(
          mm => tail.masterModelId === mm.masterModelId,
        )
        const masterModelRec = {
          masterModelName: masterModel?.text || 'N/A',
        }

        const [connectivity] = connectivityData.filter(
          item => item.tail === tail.text,
        )
        let daysOffline = 0
        if (connectivity) {
          daysOffline = daysDifference(
            new Date(connectivity.lastUpdated),
            new Date(),
          )
        }

        const issues = issueData.filter(item => item.tail.name === tail.text)
        const issuesCount = issues.length

        const [fde] = fdeData.filter(item => item.tailNumber === tail.text)
        const fdeCount = fde ? fde.fdeCount : 0
        const fdeFlightStart = fde ? fde.flightStart : null
        const fdeFormattedFlightStart = fde
          ? getFormattedDateTime(fde.flightStart)
          : null
        const fdeFlightId = fde ? fde.flightId : null
        const fdeFlightNumber = fde ? fde.flightNumber : null

        return {
          ...tail,
          ...airlineRec,
          ...masterModelRec,
          name: tail.text,
          issuesCount,
          fdeCount,
          fdeFlightStart,
          fdeFormattedFlightStart,
          fdeFlightId,
          fdeFlightNumber,
          daysOffline,
          searchText:
            tail.search.toLowerCase() +
            masterModelRec.masterModelName.toLowerCase(),
        }
      })

      commit(FDC.UPDATE_OPEN_MAINTENANCE_DATA, issueData)
      commit(FDC.UPDATE_FLEET_DATA, data)
    } catch (error) {
      console.log(`${[FDC.GET_FLEET_DATA]}: error ${error}`)
    }
  },

  async [FDC.GET_HEALTH_SCORE_BY_TAIL_DATA]({ commit, rootGetters }, options) {
    try {
      // get global filter, user settings and lookups data
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const airlinesData = rootGetters['user/mappedUserAirlines']
      const gfSelections = rootGetters['globalFilter/selections']
      const phmAirlinesData =
        rootGetters['user/mappedUserAirlinesWithPhmAgreements']
      const tailsData = rootGetters['lookups/mappedLuTails']

      // set allowed airlineIds - airlineIds with Phm Agreements
      const allowedAirlineIds = phmAirlinesData.map(item => item.airlineId)

      // get filtered tails
      const filteredTails = tailsData
        .filter(item => allowedAirlineIds.includes(item.airlineId))
        .filter(
          item =>
            gfSelections.length === 0 ||
            gfSelections.filter(
              selection =>
                selection.airlineId === item.airlineId &&
                selection.masterModelId === item.masterModelId,
            ).length > 0,
        )

      // get airlineNames
      const filteredAirlines = airlinesData
        .filter(
          item =>
            airlineIds.length === 0 || airlineIds.includes(item.airlineId),
        )
        .filter(item => allowedAirlineIds.includes(item.airlineId))
      const filteredAirlineIds = filteredAirlines.map(item => item.airlineId)

      // get features and filter out analytic modules
      const featureData = rootGetters['analyticModule/mappedAmcFeatures']
      const analyticModuleIds = options.analyticModuleIds || []

      const filteredFeatureData = featureData
        .filter(item => item.isRiskScore)
        .filter(item =>
          options.includeInternalScores ? true : !item.isPrivate,
        )
        .filter(
          item =>
            analyticModuleIds.length === 0 ||
            analyticModuleIds.includes(item.analyticModuleId),
        )
      const featureIds = filteredFeatureData.map(item => item.id)

      const updatedOptions = {
        airlineIds: filteredAirlineIds,
        featureIds,
      }

      const response = await services.fleet.getData(
        'healthScoreByTailData',
        updatedOptions,
        true,
      )

      // group data by tailNumber and filter out invalid tails
      const groupedTails = _groupBy(
        response.data.filter(
          item =>
            filteredTails.filter(tail => tail.text === item.tailNumber).length >
            0,
        ),
        'tailNumber',
      )

      // map over the tailNumbers and calculate lowScoreSummation and highScoreSummation
      const mapped = Object.keys(groupedTails).map((item, index) => {
        const values = Object.values(groupedTails)[index]
        const [first] = values
        const lowScoreSummation = values.reduce((total, item) => {
          const newTotal = total + item.lowScoreSummation
          return newTotal
        }, 0)
        const highScoreSummation = values.reduce((total, item) => {
          const newTotal = total + item.highScoreSummation
          return newTotal
        }, 0)
        const [tail] = tailsData.filter(tail => tail.text === item)

        return {
          tailNumber: item,
          aircraftId: first.aircraftId,
          masterModelId: tail?.masterModelId || 0,
          airlineId: tail?.airlineId || 0,
          lowScoreSummation,
          highScoreSummation,
        }
      })

      // return only name, lowScoreSummation, highScoreSummation (for chart) and sort data
      const data = mapped
        .map(item => {
          return {
            name: item.tailNumber || 'N/A',
            lowScoreSummation: parseFloat(item.lowScoreSummation.toFixed(2)),
            highScoreSummation: parseFloat(item.highScoreSummation.toFixed(2)),
          }
        })
        .sort(
          (a, b) =>
            b.highScoreSummation - a.highScoreSummation ||
            b.lowScoreSummation - a.lowScoreSummation ||
            a.name.localeCompare(b.name),
        )

      commit(FDC.UPDATE_HEALTH_SCORE_BY_TAIL_DATA, data)
    } catch (error) {
      console.log(`${FDC.GET_HEALTH_SCORE_BY_TAIL_DATA}: error ${error}`)
    }
  },

  async [FDC.GET_HEALTH_SCORE_BY_FEATURE_DATA](
    { commit, rootGetters },
    options,
  ) {
    try {
      const featureData = rootGetters['analyticModule/mappedAmcFeatures']
      const analyticModuleIds = options.analyticModuleIds || []
      const filteredFeatureData = featureData
        .filter(item => item.isRiskScore)
        .filter(item =>
          options.includeInternalScores ? true : !item.isPrivate,
        )
        .filter(
          item =>
            analyticModuleIds.length === 0 ||
            analyticModuleIds.includes(item.analyticModuleId),
        )
      const featureIds = filteredFeatureData.map(item => item.id)
      const tailNumber = options.tailNumber || ''
      const updatedOptions = {
        tailNumber,
        featureIds,
      }

      const response = await services.fleet.getData(
        'healthScoreByFeatureData',
        updatedOptions,
        true,
      )
      const data = response.data.tailRisks
        .map(item => {
          return {
            name: item.featureDisplayName,
            riskScore: parseFloat(item.healthScoreValue.toFixed(2)),
          }
        })
        .sort(
          (a, b) => b.riskScore - a.riskScore || a.name.localeCompare(b.name),
        )

      commit(FDC.UPDATE_HEALTH_SCORE_BY_FEATURE_DATA, data)
    } catch (error) {
      console.log(`${FDC.GET_HEALTH_SCORE_BY_FEATURE_DATA}: error ${error}`)
    }
  },

  async [FDC.GET_HEALTH_SCORE_TREND_DATA]({ commit, rootGetters }, options) {
    try {
      const featureData = rootGetters['analyticModule/mappedAmcFeatures']
      const [feature] = featureData.filter(
        item => item.text === options.feature,
      )
      const featureId = feature ? feature.value : 0
      const tailNumber = options.tailNumber || ''
      const isRiskScore = feature ? feature.isRiskScore : false
      const updatedOptions = {
        tailNumber,
        featureId,
        isRiskScore,
      }

      const response = await services.fleet.getData(
        'healthScoreTrendData',
        updatedOptions,
        true,
      )

      const data = response.data
      const mappedFeatureData = data.featureData.map(item => {
        return {
          ...item,
          timestamp: item.timestamp + 'Z',
        }
      })

      const mappedData = {
        ...data,
        featureData: mappedFeatureData,
      }

      commit(FDC.UPDATE_HEALTH_SCORE_TREND_DATA, mappedData)
    } catch (error) {
      console.log(`${FDC.GET_HEALTH_SCORE_TREND_DATA}: error ${error}`)
    }
  },

  async [FDC.GET_HEALTH_SCORE_PHM_ALERT_DATA](
    { commit, rootGetters },
    options,
  ) {
    try {
      const tailsData = rootGetters['lookups/mappedLuTails']
      const [filteredTail] = tailsData.filter(
        item => item.text === options.tailNumber,
      )
      const aircraftId = filteredTail?.value || 0
      const featureData = rootGetters['analyticModule/mappedAmcFeatures']
      const [feature] = featureData.filter(
        item => item.text === options.feature,
      )
      const featureId = feature ? feature.value : 0
      const updatedOptions = {
        aircraftId,
        featureId,
      }

      const response = await services.fleet.getData(
        'healthScorePhmAlertData',
        updatedOptions,
        true,
      )

      const data = response.data
        .filter(item => item.value)
        .map(item => {
          return {
            ...item,
            timestamp: item.date + 'Z',
            value: item.value,
            point: item.value,
            alert: item.alert,
            flightNumber: item.flight,
            version: item.analyticModuleVersion,
          }
        })

      commit(FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA, data)
    } catch (error) {
      console.log(`${FDC.GET_HEALTH_SCORE_PHM_ALERT_DATA}: error ${error}`)
    }
  },

  async [FDC.GET_HEALTH_SCORE_MX_REC_OVERLAYS_DATA]({ commit }, options) {
    try {
      const response = await services.fleet.getData(
        'healthScoreMxRecOverlaysData',
        options,
        true,
      )
      const data = response.data
      commit(FDC.UPDATE_HEALTH_SCORE_MX_REC_OVERLAYS_DATA, data)
    } catch (error) {
      console.log(
        `${FDC.GET_HEALTH_SCORE_MX_REC_OVERLAYS_DATA}: error ${error}`,
      )
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
